import axios from 'axios';
import { API_URL, GUEST_TOKEN } from '../config';

// 创建 axios 实例
const ideaApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 添加请求拦截器，自动添加 token
ideaApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || GUEST_TOKEN;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 片语服务
const ideaService = {
  // 获取片语列表
  getIdeas: async (page = 1, pageSize = 10) => {
    try {
      const response = await ideaApi.get('/api/ideas', {
        params: {
          'populate[]': ['media', 'authors', 'tags'],
          'sort[]': ['datetime:desc'],
          'pagination[page]': page,
          'pagination[pageSize]': pageSize
        }
      });
      return response.data;
    } catch (error) {
      console.error('获取片语列表失败:', error);
      throw error;
    }
  },

  // 获取单个片语
  getIdea: async (id) => {
    try {
      const response = await ideaApi.get(`/api/ideas/${id}`, {
        params: {
          'populate[]': ['media', 'authors', 'tags']
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('获取片语详情失败:', error);
      throw error;
    }
  },

  // 获取单个片语（用于分享链接，支持游客访问）
  getSharedIdea: async (id) => {
    try {
      // 使用游客token获取片语
      const response = await axios.get(`${API_URL}/api/ideas/${id}?populate=*`, {
        headers: {
          'Authorization': `Bearer ${GUEST_TOKEN}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('获取分享片语失败:', error);
      throw error;
    }
  },

  // 创建新片语
  createIdea: async (ideaData) => {
    try {
      const response = await ideaApi.post('/api/ideas', {
        data: {
          content: ideaData.content,
          datetime: new Date().toISOString(),
          tags: ideaData.tags,
          media: ideaData.media,
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('创建片语失败:', error);
      throw error;
    }
  },

  // 上传媒体文件
  uploadMedia: async (file) => {
    try {
      const formData = new FormData();
      formData.append('files', file);
      
      // 使用硬编码的 token，与 IdeasPage 相同
      const token = localStorage.getItem('token') || '4be1f38edeecfd58171b4e9b84a5ee65036319b409f9d312e6d532c3ee3022feed1b2bad9e50d00c72c9ef81a1f8594ce9e5ead7e748ad79305bd359d7ce6ba3dd119680792a13c23b587be3d28616e04f9e3d61bb0cbbcaa688212e9f4f6f6fc83296910804357449a97bd78d4140130ae36b383b99d2a0d5cb8941502406c8';
      
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      
      const result = await response.json();
      return result[0];
    } catch (error) {
      console.error('上传媒体文件失败:', error);
      throw error;
    }
  },

  // 更新片语
  updateIdea: async (documentId, ideaData) => {
    try {
      //console.log('Updating idea with documentId:', documentId, 'Data:', ideaData);
      
      // 确保documentId存在
      if (!documentId) {
        throw new Error(`Invalid documentId: ${documentId}`);
      }
      
      const response = await ideaApi.put(`/api/ideas/${documentId}`, {
        data: {
          content: ideaData.content,
          tags: ideaData.tags,
          media: ideaData.media,
          publishedAt: new Date().toISOString() // 确保内容被发布
        }
      });
      
      return response.data.data;
    } catch (error) {
      console.error('更新片语失败:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  // 收集片语中所有的媒体文件ID
  collectMediaFileIds: (ideaData) => {
    const fileIds = [];

    // 安全检查，确保ideaData存在
    if (!ideaData) {
      console.warn('collectMediaFileIds: ideaData is undefined');
      return fileIds;
    }

    // 收集media字段中的媒体文件
    if (ideaData.media && Array.isArray(ideaData.media)) {
      ideaData.media.forEach((mediaItem) => {
        if (mediaItem.id) {
          fileIds.push(mediaItem.id);
        }
      });
    }

    // 检查可能的旧格式：media.data 结构
    if (ideaData.media && ideaData.media.data && Array.isArray(ideaData.media.data)) {
      ideaData.media.data.forEach((mediaItem) => {
        if (mediaItem.id) {
          fileIds.push(mediaItem.id);
        }
      });
    }

    return [...new Set(fileIds)]; // 去重
  },

  // 删除媒体文件
  deleteMedia: async (fileId) => {
    try {
      const token = localStorage.getItem('token') || GUEST_TOKEN;

      const response = await fetch(`${API_URL}/api/upload/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`删除片语媒体文件失败: ${fileId}`, {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error(`删除片语媒体文件失败: ${response.status} ${response.statusText}`);
      }

      // 检查响应是否有内容
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return { message: '删除成功' };
      }
    } catch (error) {
      console.error(`删除片语媒体文件异常: ${fileId}`, error);
      throw error;
    }
  },

  // 删除片语（包含媒体文件清理）
  deleteIdea: async (documentId) => {
    try {
      // 确保documentId存在
      if (!documentId) {
        throw new Error(`Invalid documentId: ${documentId}`);
      }

      // 首先获取片语详情，收集所有媒体文件
      const ideaResponse = await ideaApi.get(`/api/ideas/${documentId}`, {
        params: {
          populate: ['media']
        }
      });

      const ideaData = ideaResponse.data.data;
      if (!ideaData) {
        throw new Error('片语不存在');
      }

      // Strapi 5中数据直接在顶层，不再嵌套在attributes中
      const ideaAttributes = ideaData;

      // 收集所有媒体文件ID
      const mediaFileIds = ideaService.collectMediaFileIds(ideaAttributes);

      // 删除所有关联的媒体文件
      const deletePromises = mediaFileIds.map(async (fileId) => {
        try {
          await ideaService.deleteMedia(fileId);
        } catch (error) {
          console.warn(`删除片语媒体文件失败 ${fileId}:`, error);
          // 继续删除其他文件，不因为单个文件删除失败而中断
        }
      });

      // 等待所有媒体文件删除完成
      await Promise.allSettled(deletePromises);

      // 最后删除片语记录
      const response = await ideaApi.delete(`/api/ideas/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('删除片语失败:', error);
      throw error;
    }
  }
};

export default ideaService;